import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import mapboxgl from 'mapbox-gl';
import { SearchBox } from '@mapbox/search-js-react';
import CountUp from 'react-countup';
import toast from 'react-hot-toast';
import RoomIcon from '@mui/icons-material/Room';
import berthIcon from '../../assets/location.png';
import { getAvailableBerthsPublic } from '../../services';
import 'mapbox-gl/dist/mapbox-gl.css';
import './BerthMap.css';
import { CircularProgress } from '@mui/material';

export const BerthMap = () => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [berthCount, setBerthCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');

  // Initialize map with either user location or default center
  const initializeMap = (center, zoom) => {
    if (mapInstanceRef.current) return; // Prevent multiple initializations
    if (!mapContainerRef.current) return; // Container not ready yet

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_DEFAULT_PUBLIC_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: center,
      zoom: zoom,
    });

    mapInstanceRef.current = map;
    mapInstanceRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    mapInstanceRef.current.on('load', () => {
      setMapLoaded(true);
    });
  };

  // Handle when user grants location permission
  const handleLocationAllowed = (position) => {
    const { latitude, longitude } = position.coords;
    initializeMap([longitude, latitude], 3);
  };

  // Handle when user denies location permission - use default location
  const handleLocationDenied = () => {
    toast.error('Location permission denied. Using default location.');
    // Default to Amsterdam, Netherlands with world view
    initializeMap([4.8945, 52.3676], 3);
  };

  // Request user location on mount
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        handleLocationAllowed,
        handleLocationDenied
      );
    } else {
      // Browser doesn't support geolocation
      toast.error('Geolocation is not supported by your browser.');
      initializeMap([4.8945, 52.3676], 3);
    }

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Fetch berths when map loads
  useEffect(() => {
    if (!mapLoaded) return;

    const fetchBerths = async () => {
      try {
        setLoading(true);
        const res = await getAvailableBerthsPublic();

        if (res?.status === true && res?.data) {
          setBerthCount(res.data.length);
          plotBerths(res.data);
        } else {
          toast.error(res.message);
        }
      } catch (error) {
        console.error('Error fetching berths:', error);
        toast.error('Failed to load berths');
      } finally {
        setLoading(false);
      }
    };

    fetchBerths();
  }, [mapLoaded]);

  // Plot berth markers on the map (no popups)
  const plotBerths = (berthsData) => {
    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (!mapInstanceRef.current) return;

    berthsData.forEach((berth) => {
      if (!berth?.currentlongitude || !berth?.currentLatitude) return;

      const el = document.createElement('img');
      el.src = berthIcon;
      el.alt = 'berth';
      el.className = 'berth-marker';
      el.style.width = '30px';
      el.style.height = '30px';
      el.style.cursor = 'default';

      const marker = new mapboxgl.Marker(el)
        .setLngLat([berth.currentlongitude, berth.currentLatitude])
        .addTo(mapInstanceRef.current);

      markersRef.current.push(marker);
    });
  };

  // Handle search box selection - navigate map to selected location (no filtering)
  const handleSearchSelect = (result) => {
    if (!result?.features?.[0]?.geometry?.coordinates) {
      toast.error('Please select a valid location from the search results.');
      return;
    }

    const coordinates = result.features[0].geometry.coordinates;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.flyTo({
        center: coordinates,
        zoom: 4,
        essential: true,
      });
    }
  };

  // Validate search input
  const validateSearchQuery = (query) => {
    const regex = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s,.\-#/]+$/;

    if (!regex.test(query)) {
      toast.error('Please enter a valid search query.');
      return null;
    }

    return query;
  };

  return (
    <Box className="berth-map-wrapper">
      {/* Berth Counter */}
      <Box className="berth-counter" sx={{ mb: 2, textAlign: 'center' }}>
        <Typography
          variant="h6"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            fontWeight: 500
          }}
        >
          <RoomIcon fontSize="small" color="primary" />
          Currently listed berths:{' '}
          {berthCount > 0 ? (
            <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
              <CountUp start={0} end={berthCount} duration={2.5} separator="," />
            </Box>
          ) : (
            loading ? 'Loading...' : '0'
          )}
        </Typography>
      </Box>

      {/* Search Box */}
      <Box className="search-box-container" sx={{ mb: 3 }}>
        <SearchBox
          placeholder="Search for a location (e.g., Amsterdam, Netherlands)"
          accessToken={process.env.REACT_APP_MAPBOX_DEFAULT_PUBLIC_TOKEN}
          map={mapInstanceRef.current}
          mapboxgl={mapboxgl}
          value={inputValue}
          onChange={(d) => setInputValue(d)}
          interceptSearch={validateSearchQuery}
          onRetrieve={handleSearchSelect}
        />
      </Box>

      {/* Map Container */}
      <Box
        className="map-container"
        sx={{
          position: 'relative',
          width: '100%',
          height: '600px',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <div
          ref={mapContainerRef}
          className="berth-map"
          style={{ width: '100%', height: '100%' }}
        />

        {loading && (
          <Box
            className="map-loading-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 1000
            }}
          >
            <Typography variant="body1" color="text.secondary">
              <CircularProgress size={14} /> Loading map...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
